import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useFrontChatWidget, useToast } from '@kanda-libs/ks-design-library';
import { actions, useCurrentUser } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
import { APP_ENV, URLS } from 'config';
import { H } from 'highlight.run';
import { useCurrentJob, useCurrentJobLead } from 'hooks';
import useApiError from 'hooks/useApiError';
import { selectors } from 'store';
import { cleanObject } from 'utils';

import { checkHasSecondLine } from './helpers';

export interface CheckQuoteHook {
  isLoading: boolean;
  isUserLoggedIn: boolean;
}

function useCheckQuote(): CheckQuoteHook {
  const dispatch = useAppDispatch();
  const isSubmitting = useSelector(selectors.job.getIsSubmitting);
  const onError = useApiError(
    'Session has expired - an email containing a new link has been sent',
  );

  // Get auth parameters
  const { isUserLoggedIn, isValidating } = useCurrentUser();
  // Get id from URL
  const pathKey = useSelector(selectors.getPathKey);
  const id = pathKey.id as string | undefined;
  // Get history push
  const { push } = useHistory();
  // Get resend job function
  // Get error toast fiunction
  const { showError } = useToast();
  // Get job
  const { job } = useCurrentJob();
  // Get lead
  const { lead } = useCurrentJobLead();
  // Get update function for Front
  const { update } = useFrontChatWidget();
  //
  const isQuoted = Boolean(job?.quoted_to);
  //
  const isFetching = !job || (isQuoted && !lead);

  const resentRef = useRef<boolean>(false);
  // Effect checks user is logged in, if not redirects to expired, resends job
  useEffect(() => {
    // Wait until user loaded. Is logged in, return
    if (isValidating || isUserLoggedIn || resentRef.current || !id) return;
    // Resend job
    resentRef.current = true;

    dispatch(
      actions.resendJob({
        params: { id },
        onError,
        onSuccess: () => {
          push(URLS.expired);
        },
      }),
    );
  }, [isValidating, isUserLoggedIn, showError, dispatch, id, push, onError]);

  const checkRef = useRef<boolean>(false);
  // Effect checks user is on correct page
  useEffect(() => {
    if (!job || checkRef.current) return;
    const status = job?.status;
    const financeStatus = job?.finance_status;
    const jobId = job?.id;
    if (!status || !jobId || !financeStatus) return;
    if (isSubmitting || isValidating) return;
    checkRef.current = true;
    const quotedTo = job?.quoted_to;
    // If job has been sent, no finance applied, check location
    if (status === 'sent' && financeStatus === 'not_applied') {
      // If job is quoted_to (AIP) return as on correct page
      if (quotedTo) return;
      // If on reapply page, redirect
      push(URLS.job.replace(':id?', jobId));
      return;
    }
    if (financeStatus === 'finance_not_approved') {
      const hasSecondLine = checkHasSecondLine(job);
      if (hasSecondLine) return;
    }
    // For any other status combination, go to status page
    push(URLS.status.replace(':id?', jobId));
  }, [job, lead, push, isSubmitting, isValidating]);

  // Effect sends user info to Highlight
  useEffect(() => {
    if (APP_ENV === 'qa' || !job) return;
    const customer = job?.customer;
    if (!customer) return;
    const email = customer?.email;
    const name = `${customer?.first_name} ${customer?.last_name}`;
    const phone = customer?.phone;
    const metadata = cleanObject({
      name,
      phone,
    });
    H.identify(email, metadata);
  }, [job]);

  // Effect sends user info to FrontChat
  useEffect(() => {
    if (!job) return;
    const customer = job?.customer;
    if (!customer) return;
    const email = customer?.email;
    const name = `${customer?.first_name} ${customer?.last_name}`;
    const phone = customer?.phone;
    const settings = cleanObject({
      email,
      name,
      phone,
    });
    update(settings);
  }, [job, update]);

  return {
    isLoading: Boolean(isValidating || isFetching),
    isUserLoggedIn,
  };
}

export default useCheckQuote;
